/* ============================================================
   Админка · CRUD-списки витрины: Партнёры / Проекты /
   Победители / Команда федерации.

   Полноценное редактирование всех полей, которые показываются
   на публичных страницах: добавление, изменение, удаление и
   перестановка записей (порядок в списке = порядок на сайте:
   сетка карточек, подиум победителей). Любое изменение сразу
   уходит на бэкенд (PUT /api/content/<key>) через контент-стор.
   ============================================================ */
import { useState } from 'react';
import { useFedContent, uploadUrl } from '../content';
import { useAppStore } from '../../store';
import { flag } from '../data';
import { Ic } from '../icons';
import { AdmCard, AdmIconBtn, AdmPrimaryBtn, AdmImagePicker, ViewOnSiteBtn } from './ui';

// Пресеты градиентных плашек (совпадают с сидами из data.js).
const GRADS = [
  'linear-gradient(140deg,#E11217,#8E0A0D)',
  'linear-gradient(140deg,#E6B73C,#B98B17)',
  'linear-gradient(140deg,#2A6FDB,#123a7a)',
  'linear-gradient(140deg,#1F8A5B,#0d4a30)',
  'linear-gradient(140deg,#7A3AE1,#3a1a78)',
  'linear-gradient(140deg,#E1602A,#8a3212)',
  'linear-gradient(140deg,var(--fed-surface-3),var(--fed-elevated))',
];
const GRAD_NEUTRAL = GRADS[6];

// Иконки для карточек проектов (подмножество из icons.jsx).
const PROJECT_ICONS = ['layers', 'backpack', 'cap', 'book', 'video', 'building', 'trophy', 'ball', 'gamepad', 'community', 'globe', 'diamond', 'zap', 'sprout', 'scale', 'medal'];

// Место победителя → класс подиума + подпись по умолчанию.
const PLACES = {
  1: { cls: 'gold', lab_ru: 'ЧЕМПИОН', lab_kz: 'ЧЕМПИОН' },
  2: { cls: 'silver', lab_ru: 'СЕРЕБРО', lab_kz: 'КҮМІС' },
  3: { cls: 'bronze', lab_ru: 'БРОНЗА', lab_kz: 'ҚОЛА' },
};
// Стандартные подписи — при смене места перезаписываем только их, кастом не трогаем.
const PRESET_LABS = new Set(['', 'ЧЕМПИОН', 'СЕРЕБРО', 'КҮМІС', 'БРОНЗА', 'ҚОЛА']);
const MEDAL_BG = {
  gold: 'linear-gradient(140deg,#E6B73C,#B98B17)',
  silver: 'linear-gradient(140deg,#B9C2CC,#7c8790)',
  bronze: 'linear-gradient(140deg,#D89B62,#8a5a30)',
};

// Инициалы для плашки/аватара: чистим пунктуацию, одно слово → первые n букв,
// несколько → первые буквы первых n слов. Никаких кавычек/скобок в бейдже.
const initials = (s, n = 2) => {
  const words = (s || '').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  if (!words.length) return '';
  const src = words.length === 1 ? words[0].slice(0, n) : words.map((w) => w[0]).slice(0, n).join('');
  return src.toUpperCase();
};

// Инициалы на плашке — не больше трёх символов: остальное не влезает в круг.
const mono3 = (s) => (s || '').trim().slice(0, 3).toUpperCase();

// ============ конфигурация по типу списка ============
const CFG = {
  partners: {
    add: 'Добавить партнёра',
    req: 'name',
    blank: () => ({ name: '', role_ru: 'Партнёр', role_kz: 'Серіктес', mono: '', badge: GRADS[2], logo: '', link: '' }),
    fields: [
      { k: 'name', label: 'Название', ph: 'ОО «Ecotime»', full: true },
      { k: 'role_ru', label: 'Роль (рус)', ph: 'Организатор' },
      { k: 'role_kz', label: 'Роль (қаз)', ph: 'Ұйымдастырушы' },
      { k: 'link', label: 'Ссылка партнёра', ph: 'https://…', full: true },
      { k: 'mono', label: 'Инициалы на плашке', ph: 'авто из названия' },
      { k: 'badge', label: 'Цвет плашки (пока нет логотипа)', type: 'grad', full: true },
      { k: 'logo', label: 'Логотип', type: 'image' },
    ],
    finalize: (d) => ({
      ...d,
      mono: mono3(d.mono) || initials(d.name, 2),
      role_kz: d.role_kz || d.role_ru,
      badge: d.badge || GRAD_NEUTRAL,
    }),
    title: (x) => x.name || '—',
    sub: (x) => x.role_ru || '',
    badge: (x) => ({ text: mono3(x.mono) || initials(x.name) || '?', bg: x.badge || GRAD_NEUTRAL, logo: x.logo }),
  },

  projects: {
    add: 'Добавить проект',
    req: 'title_ru',
    blank: () => ({ ic: 'layers', status: 'soon', title_ru: '', title_kz: '', d_ru: '', d_kz: '', meta_ru: '2026', meta_kz: '2026', cover: '', bodyText_ru: '', bodyText_kz: '', link: '' }),
    fields: [
      { k: 'title_ru', label: 'Название (рус)', ph: 'Школьная лига' },
      { k: 'title_kz', label: 'Название (қаз)', ph: 'Мектеп лигасы' },
      { k: 'd_ru', label: 'Описание (рус)', type: 'area', full: true },
      { k: 'd_kz', label: 'Описание (қаз)', type: 'area', full: true },
      { k: 'meta_ru', label: 'Метка (рус)', ph: '12+ школ' },
      { k: 'meta_kz', label: 'Метка (қаз)', ph: '12+ мектеп' },
      { k: 'status', label: 'Статус', type: 'select', opts: [['active', 'Активен'], ['soon', 'Анонс']] },
      { k: 'link', label: 'Внешняя ссылка (необязательно)', ph: 'https://…' },
      { k: 'bodyText_ru', label: 'Текст страницы (рус) — абзацы через пустую строку', type: 'area', full: true, rows: 6 },
      { k: 'bodyText_kz', label: 'Бет мәтіні (қаз)', type: 'area', full: true, rows: 6 },
      { k: 'ic', label: 'Иконка', type: 'icon' },
      { k: 'cover', label: 'Обложка страницы', type: 'image' },
    ],
    // текст страницы храним абзацами, как у новостей
    finalize: (d) => {
      const paras = (t) => String(t || '').split(/\n{2,}/).map((x) => x.trim()).filter(Boolean);
      const { bodyText_ru, bodyText_kz, ...rest } = d;
      const ru = paras(bodyText_ru);
      const kz = paras(bodyText_kz);
      return {
        ...rest,
        title_kz: d.title_kz || d.title_ru,
        d_kz: d.d_kz || d.d_ru,
        meta_kz: d.meta_kz || d.meta_ru,
        body_ru: ru,
        body_kz: kz.length ? kz : ru,
      };
    },
    // в редактор абзацы разворачиваем обратно в текстовое поле
    toDraft: (x) => ({
      ...x,
      bodyText_ru: (x.body_ru || []).join('\n\n'),
      bodyText_kz: (x.body_kz || []).join('\n\n'),
    }),
    viewRoute: (x) => `project/${x.id}`,
    title: (x) => x.title_ru || '—',
    sub: (x) => `${x.status === 'active' ? 'Активен' : 'Анонс'}${x.meta_ru ? ' · ' + x.meta_ru : ''}`,
    badge: (x) => ({ text: <Ic name={x.ic || 'layers'} size={20} />, bg: 'linear-gradient(140deg,var(--fed-accent),var(--fed-accent-deep))' }),
  },

  winners: {
    add: 'Добавить победителя',
    req: 'team',
    noReorder: true,
    hint: 'подиум расставляется по месту: 🥇 в центре',
    blank: () => ({ place: 1, cls: 'gold', lab_ru: 'ЧЕМПИОН', lab_kz: 'ЧЕМПИОН', team: '', cc: '', player: '', note_ru: '', note_kz: '' }),
    fields: [
      { k: 'place', label: 'Место', type: 'place' },
      { k: 'team', label: 'Команда / страна', ph: 'Босния' },
      { k: 'player', label: 'Игрок', ph: 'Влад А.' },
      { k: 'cc', label: 'Код страны (флаг)', type: 'flag', ph: 'ba · kr · kz' },
      { k: 'lab_ru', label: 'Подпись (рус)', ph: 'ЧЕМПИОН' },
      { k: 'lab_kz', label: 'Подпись (қаз)', ph: 'ЧЕМПИОН' },
      { k: 'note_ru', label: 'Примечание (рус)', ph: 'Кубок FIFA OPEN · СКО 2026', full: true },
      { k: 'note_kz', label: 'Примечание (қаз)', ph: 'FIFA OPEN · СҚО 2026 кубогы', full: true },
    ],
    finalize: (d) => {
      const place = Number(d.place) || 1;
      return {
        ...d,
        place,
        cls: (PLACES[place] || PLACES[1]).cls,
        lab_kz: d.lab_kz || d.lab_ru,
        note_kz: d.note_kz || d.note_ru,
      };
    },
    title: (x) => x.team || '—',
    sub: (x) => [x.player, x.note_ru].filter(Boolean).join(' · '),
    badge: (x) => ({ text: x.place || 1, bg: MEDAL_BG[x.cls] || MEDAL_BG.bronze }),
  },

  team: {
    add: 'Добавить сотрудника',
    req: 'name_ru',
    blank: () => ({ name_ru: '', name_kz: '', role_ru: '', role_kz: '', mono: '', photo: '' }),
    fields: [
      { k: 'name_ru', label: 'Имя (рус)', ph: 'Иван Иванов' },
      { k: 'name_kz', label: 'Имя (қаз)', ph: 'Иван Иванов' },
      { k: 'role_ru', label: 'Роль (рус)', ph: 'Директор турниров' },
      { k: 'role_kz', label: 'Роль (қаз)', ph: 'Турнир директоры' },
      { k: 'mono', label: 'Инициалы на аватаре (до 3 символов)', ph: 'авто из имени' },
      { k: 'photo', label: 'Фото', type: 'image', round: true },
    ],
    finalize: (d) => ({
      ...d,
      name_kz: d.name_kz || d.name_ru,
      role_kz: d.role_kz || d.role_ru,
      // инициалы режем: длинная строка вылезала за круглый аватар и в
      // админке (44px), и на странице «О федерации» (74px)
      mono: mono3(d.mono) || initials(d.name_ru, 2),
    }),
    title: (x) => x.name_ru || '—',
    sub: (x) => x.role_ru || '',
    badge: (x) => ({ text: mono3(x.mono) || initials(x.name_ru, 1) || '?', bg: 'linear-gradient(140deg,var(--fed-accent),var(--fed-accent-deep))', logo: x.photo, round: true }),
  },
};

// ============ поле формы ============
function Field({ f, draft, set }) {
  const val = draft[f.k] ?? '';

  if (f.type === 'image') {
    return (
      <AdmImagePicker
        label={f.label}
        value={val}
        onChange={(path) => set(f.k, path)}
        hint="Необязательно — без картинки показывается плашка с инициалами."
        preview={f.round ? { w: 56, h: 56, radius: '50%' } : undefined}
      />
    );
  }

  if (f.type === 'grad') {
    return (
      <div className="flex flex-col gap-[7px]" style={{ gridColumn: '1 / -1' }}>
        <span className="fed-label">{f.label}</span>
        <div className="flex flex-wrap gap-2">
          {GRADS.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => set(f.k, g)}
              title="Выбрать цвет"
              className="rounded-xl cursor-pointer"
              style={{ width: 44, height: 34, background: g, border: val === g ? '2px solid var(--fed-accent-bright)' : '2px solid transparent', boxShadow: val === g ? '0 0 0 2px var(--fed-bg)' : 'none' }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (f.type === 'icon') {
    return (
      <div className="flex flex-col gap-[7px]" style={{ gridColumn: '1 / -1' }}>
        <span className="fed-label">{f.label}</span>
        <div className="flex flex-wrap gap-2">
          {PROJECT_ICONS.map((ic) => {
            const on = val === ic;
            return (
              <button
                key={ic}
                type="button"
                onClick={() => set(f.k, ic)}
                title={ic}
                className="flex items-center justify-center rounded-xl cursor-pointer"
                style={{ width: 40, height: 40, background: on ? 'rgba(225,18,23,.14)' : 'var(--fed-surface-3)', border: on ? '1px solid var(--fed-line-accent)' : '1px solid var(--fed-line)', color: on ? 'var(--fed-accent-bright)' : 'var(--fed-fg-muted)' }}
              >
                <Ic name={ic} size={19} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (f.type === 'place') {
    return (
      <label className="flex flex-col gap-[7px]">
        <span className="fed-label">{f.label}</span>
        <select className="fed-input" value={String(val || 1)} onChange={(e) => set('__place', e.target.value)}>
          <option value="1">🥇 1 место · золото</option>
          <option value="2">🥈 2 место · серебро</option>
          <option value="3">🥉 3 место · бронза</option>
        </select>
      </label>
    );
  }

  if (f.type === 'select') {
    return (
      <label className="flex flex-col gap-[7px]">
        <span className="fed-label">{f.label}</span>
        <select className="fed-input" value={val} onChange={(e) => set(f.k, e.target.value)}>
          {f.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
    );
  }

  if (f.type === 'flag') {
    return (
      <label className="flex flex-col gap-[7px]">
        <span className="fed-label">{f.label}</span>
        <div className="flex items-center gap-2.5">
          <input
            className="fed-input"
            style={{ flex: 1 }}
            placeholder={f.ph}
            value={val}
            onChange={(e) => set(f.k, e.target.value.trim().toLowerCase())}
          />
          {val
            ? <img key={val} src={flag(val)} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: 30, borderRadius: 3, flexShrink: 0, boxShadow: '0 0 0 1px rgba(255,255,255,.14)' }} />
            : <span className="flex items-center justify-center rounded-[3px] text-fg-faint" style={{ width: 30, height: 20, background: 'var(--fed-surface-3)', fontSize: 10, flexShrink: 0 }}>—</span>}
        </div>
      </label>
    );
  }

  const common = {
    className: 'fed-input',
    placeholder: f.ph || '',
    value: val,
    onChange: (e) => set(f.k, e.target.value),
  };
  return (
    <label className="flex flex-col gap-[7px]" style={f.full ? { gridColumn: '1 / -1' } : undefined}>
      <span className="fed-label">{f.label}</span>
      {f.type === 'area' ? <textarea rows={f.rows || 3} {...common} /> : <input {...common} />}
    </label>
  );
}

// ============ редактор записи ============
function Editor({ listKey, cfg, draft, setDraft, onSave, onCancel, busy }) {
  const isNew = !draft.id;

  const set = (k, v) => {
    if (k === '__place') {
      const place = Number(v) || 1;
      const preset = PLACES[place];
      setDraft((d) => ({
        ...d,
        place,
        cls: preset.cls,
        lab_ru: PRESET_LABS.has((d.lab_ru || '').trim()) ? preset.lab_ru : d.lab_ru,
        lab_kz: PRESET_LABS.has((d.lab_kz || '').trim()) ? preset.lab_kz : d.lab_kz,
      }));
      return;
    }
    setDraft((d) => ({ ...d, [k]: v }));
  };

  const canSave = String(draft[cfg.req] || '').trim().length > 0;

  return (
    <AdmCard style={{ padding: 22 }}>
      <div className="flex items-center justify-between border-b border-line" style={{ paddingBottom: 14, marginBottom: 16 }}>
        <div className="font-display font-bold uppercase text-fg" style={{ fontSize: 15, letterSpacing: '.02em' }}>
          {isNew ? cfg.add : 'Редактирование'}
        </div>
        <button onClick={onCancel} className="fed-iconbtn" title="Закрыть"><Ic name="x" size={16} /></button>
      </div>

      <div className="fed-grid-form">
        {cfg.fields.map((f) => <Field key={f.k} f={f} draft={draft} set={set} />)}
      </div>

      <div className="flex gap-2.5 border-t border-line" style={{ paddingTop: 16, marginTop: 18 }}>
        <AdmPrimaryBtn icon="check" onClick={onSave} className={canSave && !busy ? '' : 'opacity-50 pointer-events-none'}>
          {busy ? 'Сохраняем…' : isNew ? 'Добавить' : 'Сохранить'}
        </AdmPrimaryBtn>
        <button onClick={onCancel} className="fed-btn-ghost h-10 px-5 rounded-[10px]" style={{ fontSize: 13 }}>Отмена</button>
      </div>
    </AdmCard>
  );
}

// ============ основной компонент ============
export default function AdmList({ listKey }) {
  const items = useFedContent((s) => s[listKey]) || [];
  const saveListItem = useFedContent((s) => s.saveListItem);
  const deleteListItem = useFedContent((s) => s.deleteListItem);
  const moveListItem = useFedContent((s) => s.moveListItem);
  const fire = useAppStore((s) => s.fire);
  const cfg = CFG[listKey];
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  // Форму закрываем только после успешной публикации — иначе запись живёт
  // лишь в localStorage и исчезает на следующей загрузке витрины.
  const save = async () => {
    if (busy || !String(draft[cfg.req] || '').trim()) return;
    setBusy(true);
    const ok = await saveListItem(listKey, cfg.finalize(draft));
    setBusy(false);
    if (ok) { fire && fire('Сохранено', 'ok'); setDraft(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Удалить запись?')) return;
    const ok = await deleteListItem(listKey, id);
    if (ok) fire && fire('Запись удалена', 'ok');
  };

  return (
    <div className="fed-admin-view flex flex-col gap-4">
      {draft ? (
        <Editor listKey={listKey} cfg={cfg} draft={draft} setDraft={setDraft} onSave={save} onCancel={() => setDraft(null)} busy={busy} />
      ) : (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-fg-muted" style={{ fontSize: 13 }}>
            Записей: <b className="text-fg">{items.length}</b> · {cfg.hint || 'порядок в списке = порядок на сайте'}
          </div>
          <AdmPrimaryBtn onClick={() => setDraft(cfg.blank())}>{cfg.add}</AdmPrimaryBtn>
        </div>
      )}

      <AdmCard style={{ padding: 20 }}>
        <div className="flex flex-col">
          {items.map((x, i) => {
            const b = cfg.badge(x);
            return (
              <div key={x.id ?? i} className="flex items-center gap-3.5 border-t border-line first:border-t-0" style={{ padding: '12px 0' }}>
                <span className="flex items-center justify-center font-mono font-bold text-white flex-shrink-0 overflow-hidden" style={{ width: 44, height: 44, fontSize: 15, borderRadius: b.round ? '50%' : 12, background: b.logo ? 'var(--fed-surface-3)' : b.bg }}>
                  {b.logo ? <img src={uploadUrl(b.logo)} alt="" className="w-full h-full" style={{ objectFit: 'cover' }} /> : b.text}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-fg truncate" style={{ fontSize: 14.5 }}>{cfg.title(x)}</div>
                  {cfg.sub(x) ? <div className="text-fg-muted truncate" style={{ fontSize: 12, marginTop: 2 }}>{cfg.sub(x)}</div> : null}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {cfg.viewRoute && <ViewOnSiteBtn route={cfg.viewRoute(x)} title="Смотреть страницу" />}
                  {!cfg.noReorder && (<>
                    <button
                      className="fed-iconbtn" title="Выше"
                      onClick={() => moveListItem(listKey, x.id, -1)}
                      disabled={i === 0}
                      style={i === 0 ? { opacity: 0.3, cursor: 'default' } : undefined}
                    >
                      <Ic name="chevD" size={16} style={{ transform: 'rotate(180deg)' }} />
                    </button>
                    <button
                      className="fed-iconbtn" title="Ниже"
                      onClick={() => moveListItem(listKey, x.id, 1)}
                      disabled={i === items.length - 1}
                      style={i === items.length - 1 ? { opacity: 0.3, cursor: 'default' } : undefined}
                    >
                      <Ic name="chevD" size={16} />
                    </button>
                  </>)}
                  <AdmIconBtn name="edit" title="Редактировать" onClick={() => setDraft(cfg.toDraft ? cfg.toDraft(x) : { ...x })} />
                  <AdmIconBtn name="trash" tint="danger" title="Удалить" onClick={() => remove(x.id)} />
                </div>
              </div>
            );
          })}
          {!items.length && <div className="text-fg-faint text-center" style={{ padding: '24px 0', fontSize: 13 }}>Список пуст — добавьте первую запись</div>}
        </div>
      </AdmCard>
    </div>
  );
}
